import { useEffect } from "react";
import { LOCALE_CONFIG, LOCALE_ORDER, SITE_URL } from "../i18n/landingLocales";
import { SUPPORTED_COUNTRIES, TIPLY_FEE } from "../seo/siteData";

function addMeta(attribute, key, content) {
    const element = document.createElement("meta");
    element.setAttribute(attribute, key);
    element.setAttribute("content", content);
    element.dataset.tiplySeo = "true";
    document.head.appendChild(element);
}

function addLink(rel, href, extra = {}) {
    const element = document.createElement("link");
    element.setAttribute("rel", rel);
    element.setAttribute("href", href);
    Object.entries(extra).forEach(([key, value]) => element.setAttribute(key, value));
    element.dataset.tiplySeo = "true";
    document.head.appendChild(element);
}

export default function SeoHead({
    locale = "en",
    title,
    description,
    canonicalPath,
    faqs = [],
    localizedAlternates = true,
    schemaType = "Service",
}) {
    useEffect(() => {
        document.head.querySelectorAll("[data-tiply-seo='true']").forEach((node) => node.remove());

        const config = LOCALE_CONFIG[locale] || LOCALE_CONFIG.en;
        const canonicalUrl = new URL(canonicalPath, SITE_URL).toString();
        document.title = title;
        document.documentElement.lang = config.hreflang;

        addMeta("name", "description", description);
        addMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
        addMeta("property", "og:type", "website");
        addMeta("property", "og:site_name", "Tiply");
        addMeta("property", "og:title", title);
        addMeta("property", "og:description", description);
        addMeta("property", "og:url", canonicalUrl);
        addMeta("property", "og:image", `${SITE_URL}/logo860.png`);
        addMeta("property", "og:locale", config.hreflang.replace("-", "_"));
        addMeta("name", "twitter:card", "summary_large_image");
        addMeta("name", "twitter:title", title);
        addMeta("name", "twitter:description", description);
        addMeta("name", "twitter:image", `${SITE_URL}/logo860.png`);
        addLink("canonical", canonicalUrl);

        if (localizedAlternates) {
            LOCALE_ORDER.forEach((code) => {
                const alternate = LOCALE_CONFIG[code];
                addLink("alternate", new URL(alternate.path, SITE_URL).toString(), { hreflang: alternate.hreflang });
                if (code !== locale) addMeta("property", "og:locale:alternate", alternate.hreflang.replace("-", "_"));
            });
            addLink("alternate", `${SITE_URL}/`, { hreflang: "x-default" });
        }

        const graph = [
            {
                "@type": "Organization",
                "@id": `${SITE_URL}/#organization`,
                name: "Tiply OÜ",
                url: `${SITE_URL}/`,
                logo: `${SITE_URL}/logo860.png`,
                email: "team@tiply.ee",
                address: {
                    "@type": "PostalAddress",
                    streetAddress: "Juhkentali 8",
                    postalCode: "10132",
                    addressLocality: "Tallinn",
                    addressCountry: "EE",
                },
            },
            {
                "@type": "WebSite",
                "@id": `${SITE_URL}/#website`,
                url: `${SITE_URL}/`,
                name: "Tiply",
                publisher: { "@id": `${SITE_URL}/#organization` },
                inLanguage: config.hreflang,
            },
        ];

        if (schemaType === "Service") {
            graph.push({
                "@type": "Service",
                "@id": `${canonicalUrl}#service`,
                name: title,
                description,
                url: canonicalUrl,
                provider: { "@id": `${SITE_URL}/#organization` },
                serviceType: "Digital tipping platform",
                areaServed: SUPPORTED_COUNTRIES.map((name) => ({ "@type": "Country", name })),
                termsOfService: `${SITE_URL}/terms/`,
                offers: {
                    "@type": "Offer",
                    priceCurrency: "EUR",
                    description: `No monthly or setup fee. ${TIPLY_FEE} is deducted from each tip.`,
                },
            });
        } else {
            graph.push({
                "@type": "WebPage",
                "@id": `${canonicalUrl}#webpage`,
                name: title,
                description,
                url: canonicalUrl,
                isPartOf: { "@id": `${SITE_URL}/#website` },
                inLanguage: config.hreflang,
            });
        }

        if (faqs.length) {
            graph.push({
                "@type": "FAQPage",
                "@id": `${canonicalUrl}#faq`,
                mainEntity: faqs.map(({ question, answer }) => ({
                    "@type": "Question",
                    name: question,
                    acceptedAnswer: { "@type": "Answer", text: answer },
                })),
            });
        }

        const structuredData = document.createElement("script");
        structuredData.type = "application/ld+json";
        structuredData.dataset.tiplySeo = "true";
        structuredData.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@graph": graph,
        });
        document.head.appendChild(structuredData);
        document.body.dataset.seoReady = "true";

        return () => {
            delete document.body.dataset.seoReady;
        };
    }, [canonicalPath, description, faqs, locale, localizedAlternates, schemaType, title]);

    return null;
}
